"use client";

import type React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ProductReviewFormSchema, TProductReviewForm } from "@/types/productv2";
import { Button } from "@/components/new/button";
import { ArrowRight, Loader2Icon } from "lucide-react";
import toast from "react-hot-toast";
import Image from "next/image";
import { Input } from "@/components/new/input";
import { Textarea } from "@/components/new/textarea";
import { Label } from "@/components/new/label";
import { SelectDropdown } from "@/components/ui/select-dropdown";
import { TMultiSelectDropdownItem } from "@/components/ui";
import { RatingInput } from "./rating-input";
import { UsageTimelineSection } from "./usage-timeline";
import { UserDetailsSection } from "./user-details";
import { TCustomUserSession } from "@/types/app";
import { useQueryClient } from "@tanstack/react-query";
import { PRODUCTV2_API } from "@/lib/constants";

type ProductReviewFormProps = {
	productId: string;
	productSlug: string;
	productName: string;
	companyName: string;
	defaultValues?: Partial<TProductReviewForm>;
	onClose?: () => void;
	onSuccess?: () => void;
	user?: TCustomUserSession | null | undefined;
};

export default function ProductReviewForm({
	productId,
	productSlug,
	productName,
	companyName,
	defaultValues,
	onClose,
	onSuccess,
	user,
}: ProductReviewFormProps) {
	const {
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { isSubmitting, errors },
	} = useForm<TProductReviewForm>({
		resolver: zodResolver(ProductReviewFormSchema),
		defaultValues: {
			supportRating: 0,
			functionalityRating: 0,
			valueRating: 0,
			overallRating: 0,
			productId: productId,
			...defaultValues,
			startDate: defaultValues?.startDate ? String(defaultValues.startDate) : undefined,
			endDate: defaultValues?.endDate ? String(defaultValues.endDate) : undefined,
		},
	});
	const queryClient = useQueryClient();

	const [, setStartDateOpen] = useState(false);
	const [, setEndDateOpen] = useState(false);
	const supportRating = watch("supportRating");
	const valueRating = watch("valueRating");
	const functionalityRating = watch("functionalityRating");
	const startDate = watch("startDate");
	const endDate = watch("endDate");
	const usageFrequency = watch("usageFrequency");
	const reviewAs = watch("reviewAs");
	const [isOngoing, setIsOngoing] = useState<boolean>(() => !endDate);

	const UsageFrequencyItems: TMultiSelectDropdownItem[] = [
		{ id: "ALL_THE_TIME", name: "All the time" },
		{ id: "EVERY_DAY", name: "Every day" },
		{ id: "FEW_TIMES_IN_A_WEEK", name: "Few times in a week" },
		{ id: "FEW_TIMES_IN_A_MONTH", name: "Few times in a month" },
		{ id: "FEW_TIMES_IN_A_YEAR", name: "Few times in a year" },
		{ id: "ONLY_1_3_TIMES", name: "Only 1-3 times" },
		{ id: "NOT_SURE", name: "Not sure" },
	];

	const employeeLabel =
		companyName && productName
			? `Employee of the Company (${companyName} - ${productName})`
			: companyName
			? `Employee of the Company (${companyName})`
			: productName
			? `Employee of the Company (${productName})`
			: "Employee of the Company";

	const ReviewAsItems: TMultiSelectDropdownItem[] = [
		{ id: "INDIVIDUAL_USER", name: "Individual User of the product" },
		{ id: "COMPANY_USER", name: "Company User" },
		{ id: "EMPLOYEE_OF_COMPANY", name: employeeLabel },
		{ id: "CLIENT_PARTNER", name: "Client/Partner of the Company" },
	];

	const onSubmit = async (data: TProductReviewForm) => {
		try {
			const res = await fetch(`${PRODUCTV2_API}/${productSlug}/reviews`, {
				method: "POST",
				body: JSON.stringify(data),
				headers: { "Content-Type": "application/json" },
				credentials: "include",
			});
			const result = await res.json();
			if (!result.success) {
				throw new Error("Failed to submit review");
			}
			toast.success("Review submitted successfully");
			queryClient.invalidateQueries({ queryKey: ["reviews", productSlug] });
			queryClient.invalidateQueries({ queryKey: ["productRating", productSlug] });
			if (onSuccess) onSuccess();
			if (onClose) onClose();
		} catch (error) {
			console.error(error);
			toast.error("Something went wrong");
		}
	};

	return (
		<div className="w-full max-w-5xl rounded-2xl bg-blue-50 p-6 sm:p-10">
			<form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center">
					<Image
						src="/images/product-review-header.png"
						alt="Product Review Form"
						className="h-[100px] w-[140px] rounded-lg object-cover"
						width={400}
						height={200}
					/>
					<div className="flex flex-col gap-1">
						<h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Write a Review</h1>
						<p className="text-sm text-gray-500">
							Fields marked with an asterisk (<span className="text-red-500">*</span>) are required.
						</p>
					</div>
				</div>

				<div className="space-y-1">
					<Label className="text-base font-medium text-gray-700">
						Title <span className="text-red-500">*</span>
					</Label>
					<Input {...register("title")} />
					<p className="text-sm text-gray-400">Short and descriptive title of your review</p>
					{errors.title && <p className="text-sm text-red-500">{String(errors.title.message)}</p>}
				</div>

				<div className="space-y-1">
					<Label className="text-base font-medium text-gray-700">
						Summary <span className="text-red-500">*</span>
					</Label>
					<Textarea {...register("summary")} />
					<p className="text-sm text-gray-400">
						Write a detailed review about your experience with the product.
					</p>
					{errors.summary && (
						<p className="text-sm text-red-500">{String(errors.summary.message)}</p>
					)}
				</div>

				<div className="space-y-2">
					<Label className="text-base font-semibold text-gray-800">
						Quick Rating <span className="text-red-500">*</span>
					</Label>
					<div className="grid gap-6">
						<RatingInput
							title="How was the Onboarding Experience & Customer Support?"
							rating={supportRating}
							onChange={(value) => {
								setValue("supportRating", value);
								const overall = Number(
									((value + watch("valueRating") + watch("functionalityRating")) / 3).toFixed(1)
								);
								setValue("overallRating", overall);
							}}
							error={errors.supportRating?.message}
						/>
						<RatingInput
							title="Did you find us Value for money?"
							rating={valueRating}
							onChange={(value) => {
								setValue("valueRating", value);
								const overall = Number(
									((watch("supportRating") + value + watch("functionalityRating")) / 3).toFixed(1)
								);
								setValue("overallRating", overall);
							}}
							error={errors.valueRating?.message}
						/>
						<RatingInput
							title="How would you rate the functionality and ease of use?"
							rating={functionalityRating}
							onChange={(value) => {
								setValue("functionalityRating", value);
								const overall = Number(
									((watch("supportRating") + watch("valueRating") + value) / 3).toFixed(1)
								);
								setValue("overallRating", overall);
							}}
							error={errors.functionalityRating?.message}
						/>
					</div>
				</div>

				<div className="space-y-2">
					<Label className="text-base font-medium text-gray-700">
						Usage Frequency <span className="text-red-500">*</span>
					</Label>
					<SelectDropdown
						items={UsageFrequencyItems}
						selectedItem={usageFrequency}
						setActiveItems={(value) => setValue("usageFrequency", value)}
						buttonLabel="Select how frequently you use the product"
					/>
					{errors.usageFrequency && (
						<p className="text-sm text-red-500">{String(errors.usageFrequency.message)}</p>
					)}
				</div>

				<UsageTimelineSection
					startDate={startDate}
					endDate={endDate}
					isOngoing={isOngoing}
					setIsOngoing={setIsOngoing}
					setStartDateOpen={setStartDateOpen}
					setEndDateOpen={setEndDateOpen}
					setValue={setValue}
					errors={errors}
				/>

				<div className="space-y-4">
					<h3 className="text-xl font-semibold text-gray-800">Additional Review Questions</h3>
					<div className="space-y-1">
						<Label className="text-base font-medium text-gray-700">
							What is your primary use case?
						</Label>
						<Textarea {...register("additionalQuestions.useCase")} />
					</div>
					<div className="space-y-1">
						<Label className="text-base font-medium text-gray-700">
							What do you like most about it?
						</Label>
						<Textarea {...register("additionalQuestions.favouriteAspect")} />
					</div>
					<div className="space-y-1">
						<Label className="text-base font-medium text-gray-700">
							What could be done better?
						</Label>
						<Textarea {...register("additionalQuestions.improvements")} />
					</div>
				</div>

				<div className="space-y-2">
					<Label className="text-base font-medium text-gray-700">
						Review as <span className="text-red-500">*</span>
					</Label>
					<SelectDropdown
						items={ReviewAsItems}
						selectedItem={reviewAs}
						setActiveItems={(value) => setValue("reviewAs", value)}
						buttonLabel="Select reviewer type"
					/>
					{errors.reviewAs && (
						<p className="text-sm text-red-500">{String(errors.reviewAs.message)}</p>
					)}
				</div>

				<UserDetailsSection register={register} errors={errors} user={user} />

				<div className="pt-2">
					<Button
						type="submit"
						className="inline-flex w-full items-center justify-center rounded-md bg-blue-600 px-6 py-2 text-white transition hover:bg-blue-700 disabled:opacity-60 sm:w-auto"
						disabled={isSubmitting}
					>
						{isSubmitting ? "Submitting..." : "Submit your review"}
						{isSubmitting ? (
							<Loader2Icon className="ml-2 h-4 w-4 animate-spin" />
						) : (
							<ArrowRight className="ml-2 h-4 w-4" />
						)}
					</Button>
				</div>
			</form>
		</div>
	);
}
